import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDownIcon, CheckIcon } from './Icons';

interface MultiSelectOption {
  value: string | number;
  label: string;
}

interface MultiSelectProps {
  options?: Array<string | number | MultiSelectOption>;
  value?: Array<string | number>;
  onChange?: (value: Array<string | number>) => void;
  placeholder?: string;
  className?: string;
  maxDisplay?: number;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options...',
  className,
  maxDisplay = 2,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { colors } = useTheme();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Normalize options to handle both simple values and {value, label} objects
  const normalizedOptions: MultiSelectOption[] = options.map((opt) => {
    if (typeof opt === 'object' && opt !== null && 'value' in opt) {
      return { value: opt.value, label: opt.label || String(opt.value) };
    }
    return { value: opt, label: String(opt) };
  });

  // Create a map for quick label lookup
  const labelMap = new Map<string | number, string>();
  normalizedOptions.forEach((opt) => {
    labelMap.set(opt.value, opt.label);
  });

  const filteredOptions = normalizedOptions.filter((option) => {
    const searchLower = searchTerm.toLowerCase();
    return option.label.toLowerCase().includes(searchLower) || String(option.value).toLowerCase().includes(searchLower);
  });

  const handleToggleOption = (optionValue: string | number) => {
    const stringValue = String(optionValue);
    const newValue = value.some((v) => String(v) === stringValue || Number(v) === Number(optionValue))
      ? value.filter((v) => String(v) !== stringValue && Number(v) !== Number(optionValue))
      : [...value, optionValue];
    onChange?.(newValue);
  };

  const handleClearAll = () => {
    onChange?.([]);
  };

  const displayText = (): string => {
    if (value.length === 0) return placeholder;
    const labels = value.map((v) => labelMap.get(v) || String(v));
    if (value.length <= maxDisplay) return labels.join(', ');
    return `${labels.slice(0, maxDisplay).join(', ')} +${value.length - maxDisplay}`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          colors.surface,
          colors.text,
          colors.border,
          colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
          value.length > 0 && cn(colors.primaryBorderAll, colors.surfaceHover),
          className
        )}
      >
        <span className={cn('truncate flex-1 text-left', value.length === 0 ? colors.textMuted : colors.text)}>
          {displayText()}
        </span>
        <div className="flex items-center gap-1">
          {value.length > 0 && (
            <span className={cn(colors.primary, colors.primaryText, 'text-xs px-1.5 py-0.5 rounded-full font-medium')}>
              {value.length}
            </span>
          )}
          <ChevronDownIcon className={cn('w-4 h-4 transition-transform', isOpen ? 'rotate-180' : '')} />
        </div>
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-hidden',
            colors.surface,
            colors.border
          )}
        >
          {/* Search and controls */}
          <div className={cn('p-2 border-b', colors.border)}>
            <input
              type="text"
              placeholder="Search options..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'w-full px-2 py-1 text-xs border rounded',
                colors.surface,
                colors.text,
                colors.border,
                'focus:outline-none focus:ring-1 focus:ring-offset-0',
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : ''
              )}
            />
            {value.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className={cn('mt-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300', colors.interactiveHover)}
              >
                Clear All ({value.length})
              </button>
            )}
          </div>

          {/* Options list */}
          <div className="max-h-40 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className={cn('px-3 py-2 text-sm', colors.textMuted)}>No options found</div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.some(
                  (v) => String(v) === String(option.value) || Number(v) === Number(option.value)
                );
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleToggleOption(option.value)}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2',
                      colors.interactiveHover,
                      isSelected ? cn(colors.surfaceHover, colors.primaryFg) : colors.text
                    )}
                  >
                    <div
                      className={cn(
                        'w-4 h-4 border-2 rounded flex items-center justify-center',
                        isSelected ? cn(colors.primary, colors.primaryBorderAll) : colors.border
                      )}
                    >
                      {isSelected && <CheckIcon className={cn('w-3 h-3', colors.primaryText)} />}
                    </div>
                    <span className="flex-1">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export { MultiSelect };

